import { Box } from '@mui/material';
import {
  GridColDef, GridPaginationModel, GridRowSelectionModel, GridRowsProp,
} from '@mui/x-data-grid';
import { FC, useEffect, useState } from 'react';

import ShopContent from 'components/_ShopContent';
import ManagementSubheader from 'components/ManagementSubheader';
import AdjustInventory from 'containers/Inventory/AdjustInventory';
import { INVETORY_GRID_COLUMNS } from 'containers/Inventory/constants';
import { ToolbarWithCustomButtons } from 'containers/Inventory/CustomToolbar';
import InventoryFilterFlyout from 'containers/Inventory/InventoryFilterFlyout';
import MoveInventory from 'containers/Inventory/MoveInventory';
import { useInventoryFilter } from 'context/InventoryFilterContext';

import { postInventorySearch } from 'services/Inventory';

import { InventoryCustomAction } from 'constants/enums';
import { DEFAULT_PAGINATION_LIMIT } from 'constants/general';
import { InventoryItem, InventorySearchPayload } from 'types/inventory.interface';
import { handleApiErrors } from 'utils/errorUtils';
import { getRowClassName } from 'utils/inventory';
import { getStoreId } from 'utils/storageUtils';

import { StyledInventoryDataGrid, StyledInventoryPaper } from './index.styled';

const Inventory: FC = () => {
  // #region DataGrid State
  const columns: GridColDef[] = INVETORY_GRID_COLUMNS;
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: DEFAULT_PAGINATION_LIMIT,
  });
  const [rowInventoryItems, seRowInventoryItems] = useState<GridRowsProp>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [finalRawCount, setFinalRawCount] = useState(0);
  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>([]);
  const [rowSelectionData, setRowSelectionData] = useState<GridRowsProp>([]);
  const [disableActionButton, setDisableActionButton] = useState<boolean>(true);
  const [showActionDialog, setShowActionDialog] = useState<InventoryCustomAction | null>(null);
  // #endregion

  const {
    inventoryFilters,
  } = useInventoryFilter();

  // #region DataGrid Handlers
  const onAdjustClick = () => (setShowActionDialog(InventoryCustomAction.ADJUST));
  const onMoveClick = () => (setShowActionDialog(InventoryCustomAction.MOVE));
  const handleSelectionChange = (newRowSelectionModel: GridRowSelectionModel) => {
    setRowSelectionModel(newRowSelectionModel);
    setDisableActionButton(!newRowSelectionModel.length);
    setRowSelectionData(rowInventoryItems.filter((row) => newRowSelectionModel.includes(row.rowItemId)));
  };

  const handlePaginationModelChange = (newPaginationModel: GridPaginationModel) => {
    setPaginationModel(newPaginationModel);
    getInventory(newPaginationModel.page, newPaginationModel.pageSize);
  };

  const handleCustomActionDialogClose = () => {
    setShowActionDialog(null);
  };

  const handleCustomActionDialogSuccess = () => {
    getInventory(paginationModel.page, paginationModel.pageSize);
    setRowSelectionModel([]);
    setDisableActionButton(true);
    setShowActionDialog(null);
  };
  // #endregion

  const getInventory = async (page: number, pageSize: number) => {
    setIsLoading(true);
    try {
      const clientId = getStoreId();
      const payload: InventorySearchPayload = {
        start: page * pageSize,
        length: pageSize,
        filter: inventoryFilters || {},
      };

      const { data } = await postInventorySearch(clientId!, payload);

      if (!data.list) {
        seRowInventoryItems([]);
        return;
      }

      if (!data.hasNextPage) {
        setFinalRawCount(data.list.length + pageSize * page);
      }

      const items = data.list.map((item: InventoryItem, index: number) => ({
        ...item,
        locationLowInventoryThresholdBatch: item.locationLowInventoryThresholdBatch || 0,
        allOnHandPerLocation: item.allOnHandPerLocation || 0,
        rowItemId: `${item.itemId}-${item.areaId ? item.areaId : index - page}-${item.batchId}`,
      }));

      seRowInventoryItems(items);
      setHasNextPage(data.hasNextPage);
      setRowSelectionModel([]);
    } catch (e) {
      handleApiErrors(e);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getInventory(0, paginationModel.pageSize);
  }, []);

  useEffect(() => {
    getInventory(paginationModel.page, paginationModel.pageSize);
  }, [inventoryFilters]);

  return (
    <>
      <ManagementSubheader title="Inventory">
        <Box display="flex" justifyContent="flex-end" alignItems="center" gap={2}>
          <InventoryFilterFlyout />
        </Box>
      </ManagementSubheader>
      <ShopContent sx={{ flex: 1 }}>
        <StyledInventoryPaper>
          <StyledInventoryDataGrid
            loading={isLoading}
            rows={[...rowInventoryItems]}
            getRowId={(row) => row.rowItemId}
            getRowClassName={getRowClassName}
            columns={columns}
            slots={{
              toolbar: ToolbarWithCustomButtons(
                disableActionButton,
                onAdjustClick,
                onMoveClick,
              ),
            }}
            // filtering
            ignoreDiacritics
            disableColumnFilter
            // selection
            checkboxSelection
            onRowSelectionModelChange={handleSelectionChange}
            rowSelectionModel={rowSelectionModel}
            // pagination
            paginationMeta={{ hasNextPage }}
            paginationModel={paginationModel}
            paginationMode="server"
            rowCount={hasNextPage ? -1 : finalRawCount}
            onPaginationModelChange={handlePaginationModelChange}
          />
        </StyledInventoryPaper>
        {showActionDialog === InventoryCustomAction.ADJUST && (
          <AdjustInventory
            selectedRows={rowSelectionData}
            isOpen
            onClose={handleCustomActionDialogClose}
            onSuccess={handleCustomActionDialogSuccess}
          />
        )}
        {showActionDialog === InventoryCustomAction.MOVE && (
          <MoveInventory
            selectedRows={rowSelectionData}
            isOpen
            onClose={handleCustomActionDialogClose}
          />
        )}
      </ShopContent>
    </>
  );
};

export default Inventory;
